import { distanceMi } from './geo';
import { type PlaceLookup, stayShortName } from './items';
import type { Item } from './schema';

/**
 * When to leave the hotel for a flight, going by taxi or Uber: be at the airport two hours
 * before departure (three going abroad), plus the ride. The ride is guessed from the distance,
 * so it works offline: roads run about a third longer than the straight line, and cities are
 * slower than highways, with a few minutes for the car to arrive.
 */

const MIN = 60_000;
const H = 60 * MIN;
/** Hotels further than this from the airport aren't where the trip to it starts. */
const NEAR_MI = 60;
const ROAD_FACTOR = 1.35;
const PICKUP = 10 * MIN;

export interface LeaveForAirport {
  at: number;
  stay: Item;
  stayName: string;
  /** The guessed ride, minutes. */
  rideMin: number;
  /** How early to be at the airport, minutes before departure. */
  aheadMin: number;
}

/** The ride's minutes: slow in town, faster on longer highway runs. */
export function rideMinutes(straightMi: number): number {
  const road = straightMi * ROAD_FACTOR;
  const mph = road < 5 ? 18 : road < 15 ? 25 : road < 40 ? 35 : 45;
  return Math.round((road / mph) * 60 + PICKUP / MIN);
}

export function leaveForAirport(
  flight: Item,
  items: Item[],
  places: PlaceLookup,
): LeaveForAirport | null {
  if (flight.type !== 'flight' || flight.startAt == null) return null;
  const dep = flight.startAt;
  const airport = places(flight.startPlaceId);
  if (airport?.lat == null || airport.lon == null) return null;
  const live = items.filter((i) => !i.deleted && i.status === 'active' && i.startAt != null);
  // Driving yourself: a rental car still out around the flight.
  if (live.some((i) => i.type === 'car' && i.startAt! < dep && (i.endAt ?? 0) > dep - 6 * H))
    return null;
  // The stay you're leaving: begun before the flight, and not over long before it.
  const stay = live
    .filter((i) => i.type === 'lodging' && i.startAt! < dep - 2 * H)
    .filter((i) => i.endAt != null && i.endAt > dep - 18 * H)
    .map((i) => ({ i, p: places(i.startPlaceId) }))
    .filter(
      ({ p }) =>
        p?.lat != null &&
        p.lon != null &&
        distanceMi({ lat: p.lat, lon: p.lon }, { lat: airport.lat!, lon: airport.lon! }) <= NEAR_MI,
    )
    .sort((a, b) => b.i.startAt! - a.i.startAt!)[0];
  if (!stay?.p) return null;
  const to = places(flight.endPlaceId);
  const abroad =
    !!airport.countryCode && !!to?.countryCode && airport.countryCode !== to.countryCode;
  const aheadMin = abroad ? 180 : 120;
  const rideMin = rideMinutes(
    distanceMi({ lat: stay.p.lat!, lon: stay.p.lon! }, { lat: airport.lat, lon: airport.lon }),
  );
  // On a round five minutes, earlier rather than later.
  const at = Math.floor((dep - (aheadMin + rideMin) * MIN) / (5 * MIN)) * 5 * MIN;
  return { at, stay: stay.i, stayName: stayShortName(stay.i), rideMin, aheadMin };
}
